import { useTranslation } from 'react-i18next'
import { Box, Typography, TextField, Switch } from '@mui/material'
import { Controller } from 'react-hook-form'
import { MuiFileInput } from 'mui-file-input'
import LocalParkingOutlinedIcon from '@mui/icons-material/LocalParkingOutlined'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { colors } from '../../const/colors'
import { OrigoEventName } from '../../graphql/hid/getOrigoAccount'
import { CAR_PLATE_REGEX } from '../../const/regex'

export const accountUnderCreation = [
  OrigoEventName.NEW,
  OrigoEventName.USER_CREATED,
]

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: colors.surface,
    fontSize: '14px',
    '& fieldset': { borderColor: colors.border },
    '&:hover fieldset': { borderColor: colors.slate },
    '&.Mui-focused fieldset': {
      borderColor: colors.navy,
      borderWidth: '1.5px',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    color: colors.slate,
    '&.Mui-focused': { color: colors.navy },
  },
  '& .MuiFormHelperText-root': {
    fontSize: '12px',
    marginLeft: '4px',
  },
} as const

const SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.navy },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: colors.navy,
  },
} as const

const ICON_BOX_SX = {
  width: 34,
  height: 34,
  borderRadius: '10px',
  background: 'rgba(19,54,73,0.07)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.navy,
  flexShrink: 0,
} as const

const CardSettingsForm = ({
  control,
  errors,
  watch,
}: {
  control: any
  errors: any
  watch: any
}) => {
  const { t } = useTranslation(['hid', 'user_details', 'form'])
  const parkingAccess = watch('withParkingAccess')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <Controller
        name="withParkingAccess"
        control={control}
        defaultValue={false}
        render={({ field: { value, onChange } }) => (
          <Box
            onClick={() => onChange(!value)}
            data-e2e="parking-access-toggle"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              p: '10px 14px',
              borderRadius: '12px',
              border: `1px solid ${value ? colors.navy : colors.border}`,
              background: value ? 'rgba(19,54,73,0.04)' : colors.surface,
              cursor: 'pointer',
              transition: 'border-color .15s, background .15s',
            }}
          >
            <Box sx={ICON_BOX_SX}>
              <LocalParkingOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              sx={{
                flex: 1,
                fontSize: '14px',
                fontWeight: 600,
                color: colors.navy,
              }}
            >
              {t('with_parking_access')}
            </Typography>
            <Switch
              checked={!!value}
              onChange={() => onChange(!value)}
              onClick={e => e.stopPropagation()}
              sx={SWITCH_SX}
              size="small"
            />
          </Box>
        )}
      />

      {parkingAccess && (
        <Controller
          name="carPlate"
          control={control}
          defaultValue=""
          rules={{
            required: t('form:field_required'),
            pattern: {
              value: CAR_PLATE_REGEX,
              message: t('form:valid_car_plate'),
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('user_details:car_plate')}
              fullWidth
              variant="outlined"
              error={!!errors.carPlate}
              helperText={errors.carPlate?.message}
              inputProps={{
                'data-e2e': 'mobile-card-car-plate',
                style: {
                  textTransform: 'uppercase',
                  fontFamily: 'monospace',
                  letterSpacing: '0.06em',
                },
              }}
              sx={FIELD_SX}
            />
          )}
        />
      )}

      <Controller
        name="comment"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            label={t('comment')}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            sx={FIELD_SX}
          />
        )}
      />

      <Controller
        name="attachment"
        control={control}
        render={({ field }) => (
          <MuiFileInput
            {...field}
            label={t('add_attachment')}
            InputProps={{
              startAdornment: (
                <AttachFileIcon
                  sx={{ fontSize: 18, color: colors.slate, mr: '4px' }}
                />
              ),
            }}
            getInputText={value => (value ? value.name : '')}
            getSizeText={value =>
              value && value.size > 10_000_000 ? 'Too big!' : ''
            }
            sx={{ width: '100%', ...FIELD_SX }}
          />
        )}
      />
    </Box>
  )
}

export default CardSettingsForm
